/*
  site-mobile-drawer.js
  WHAT: Drop-down mobile nav panel — open/close, focus handling, scroll lock,
        accordion sub-categories.
  WHY:  Subscribes to the blueant:mobile-nav:open event dispatched by site-header.js when the
        burger is tapped. That event fires on EVERY tap and carries detail.expanded, so this
        file toggles rather than only opening — the burger is now the close control too.
  WHEN: Added 2026-05-28 with mobile-drawer build (feature #5).
        Rebuilt 2026-09-30 as a drop-down (owner, after comparing Bellroy and Apple).
  A11Y: Disclosure semantics, not a modal. The panel is no longer role="dialog"/aria-modal,
        because the only close control (the burger) lives OUTSIDE it and aria-modal would have
        told assistive tech to ignore exactly that control. The burger owns state via
        aria-expanded; aria-hidden here stays in step. Tab cycles the burger together with the
        panel's own links, so the close control is always reachable from the keyboard.
*/

(function () {
  'use strict';

  var drawer = document.querySelector('[data-mobile-drawer]');
  if (!drawer) return;

  var panel = drawer.querySelector('.ba-mobile-drawer__panel');
  var openerRef = null;
  var savedScrollY = 0;

  function burgerEl() {
    return document.querySelector('[data-action="open-mobile-nav"]');
  }

  function getFocusable(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  // The panel drops from directly under the header, so it needs the header's real position
  // rather than a hardcoded guess.
  //
  // Use BOTTOM, not height. The first version used height and the panel sat 36px too high,
  // covering the lower edge of the header: measured at 375px the header is 65px tall but its
  // bottom is at 101px, because the announcement bar sits above it. Height only equals offset
  // when the header is the first thing on the page, and here it is not.
  //
  // Clamped at 0 so a negative bottom — header scrolled out of view, which open() prevents but
  // resize could reintroduce — degrades to a full-height panel rather than a gap at the top.
  function anchorBelowHeader() {
    var header = document.querySelector('.ba-site-header');
    var bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    drawer.style.setProperty('--ba-drawer-top', Math.max(0, bottom) + 'px');
  }

  function open(source) {
    if (drawer.classList.contains('is-open')) return;
    openerRef = source || document.activeElement;

    // Scroll to the top BEFORE locking. The burger is the close control, so it has to be on
    // screen while the panel is open, and the header is position:relative — scroll far enough
    // and it is simply not there. The jump is invisible because the panel covers everything
    // below the header, and close() puts the reader back exactly where they were.
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    window.scrollTo(0, 0);

    anchorBelowHeader();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-mobile-drawer-open');

    // Land on the first nav link. The old close button used to take this focus; with it gone,
    // the first real destination is the useful place to be.
    var first = panel ? getFocusable(panel)[0] : null;
    if (first) first.focus();
  }

  function close(opts) {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-mobile-drawer-open');

    var burger = burgerEl();
    if (burger) burger.setAttribute('aria-expanded', 'false');

    // Restore the reader's place. Skipped when a link was followed — the browser is already
    // navigating and scrolling the outgoing page would be both pointless and visible.
    //
    // Deferred to the next frame, not run inline. Removing the class above restores overflow,
    // but the document's scrollable region is not recalculated until layout has run — call
    // scrollTo before that and the browser clamps against a document it still thinks is
    // unscrollable, landing on 0. Measured: restoring 900 returned 0 both inline and after a
    // forced reflow read; rAF is what actually works.
    if (!(opts && opts.navigating)) {
      var target = savedScrollY;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { window.scrollTo(0, target); });
      });
    }

    // preventScroll is the whole fix for the restore, not a nicety. Returning focus to the
    // burger makes the browser scroll that button into view, and the burger is at the top of
    // the document — so every restore was being undone a moment after it happened. Two earlier
    // attempts (a forced reflow, then a double rAF) failed because they addressed timing when
    // the cause was focus.
    if (openerRef && typeof openerRef.focus === 'function') {
      try {
        openerRef.focus({ preventScroll: true });
      } catch (err) {
        openerRef.focus(); // ancient browsers ignore the options object
      }
    }
    openerRef = null;
  }

  // ─── The burger toggles ────────────────────────────────────────────────────
  // site-header.js flips aria-expanded itself and reports the new state in detail.expanded, so
  // this listener follows that rather than deciding independently — one owner of the state.
  document.addEventListener('blueant:mobile-nav:open', function (e) {
    var wantOpen = !(e && e.detail && e.detail.expanded === false);
    if (wantOpen) open(burgerEl());
    else close();
  });

  // Keep the panel anchored if the header changes height under it (orientation change, or the
  // announcement bar being dismissed while the menu is open).
  window.addEventListener('resize', function () {
    if (drawer.classList.contains('is-open')) anchorBelowHeader();
  });

  // ─── Close triggers ────────────────────────────────────────────────────────
  drawer.addEventListener('click', function (e) {
    if (e.target.closest('[data-action="close-mobile-nav"]')) {
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      e.preventDefault();
      close();
    }
  });

  // ─── Focus trap ────────────────────────────────────────────────────────────
  // Tab/Shift-Tab inside the panel cycles between first and last focusable.
  //
  // Guarded, unlike before. `panel` was the only unguarded query result in the 22 BA
  // JavaScript files, and it is out of step with this file's own style two lines up
  // (`if (!drawer) return`). It does not throw today, but a class rename in the markup
  // would take the whole focus trap with it - and a drawer you cannot tab out of is an
  // accessibility failure, not a cosmetic one.
  //
  // THE BURGER IS PART OF THE CYCLE, not outside it. It is the only close control now, so a
  // trap confined to the panel would lock a keyboard user in with no way out — the precise
  // failure that made aria-modal wrong here too. Listening on the document rather than the
  // panel is what lets the cycle include a button that is not inside the panel.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    if (!drawer.classList.contains('is-open')) return;
    if (!panel) return;

    var burger = burgerEl();
    var focusable = getFocusable(panel);
    if (burger) focusable.push(burger);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // ─── Sub-category accordion ────────────────────────────────────────────────
  drawer.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-mobile-sub-toggle]');
    if (!toggle) return;
    e.preventDefault();
    var item = toggle.closest('.ba-mobile-drawer__item');
    if (!item) return;
    var expanded = toggle.getAttribute('aria-expanded') === 'true';

    // Owner 2026-09-30: "switching between categorirs should autoclose any open category."
    // Single-open accordion. Without this, opening three categories pushed the rest of the nav
    // far below the fold and the panel became a scroll. Runs BEFORE the clicked item is
    // toggled, and skips it, so collapsing an already-open row still just closes it.
    Array.prototype.forEach.call(
      drawer.querySelectorAll('.ba-mobile-drawer__item.is-open'),
      function (openItem) {
        if (openItem === item) return;
        openItem.classList.remove('is-open');
        var otherToggle = openItem.querySelector('[data-mobile-sub-toggle]');
        if (otherToggle) {
          otherToggle.setAttribute('aria-expanded', 'false');
          var otherId = otherToggle.getAttribute('aria-controls');
          var otherSub = otherId ? document.getElementById(otherId) : null;
          if (otherSub) otherSub.setAttribute('aria-hidden', 'true');
        }
      }
    );

    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    item.classList.toggle('is-open', !expanded);
    var subId = toggle.getAttribute('aria-controls');
    if (subId) {
      var sub = document.getElementById(subId);
      if (sub) sub.setAttribute('aria-hidden', expanded ? 'true' : 'false');
    }
  });

  // ─── Close on route change (Shopify Hydrogen-ish links) ────────────────────
  // Closes when the user clicks any non-toggle nav link.
  drawer.addEventListener('click', function (e) {
    var anchor = e.target.closest('a[href]');
    if (!anchor) return;
    if (anchor.closest('[data-mobile-sub-toggle]')) return;
    // Allow the navigation to start, then close after the click bubbles. Flagged as navigating
    // so close() does not restore the old scroll position on a page that is already leaving.
    setTimeout(function () { close({ navigating: true }); }, 50);
  });
})();
